import { UnitValue } from '@/helpers/unit-formatter/unit-formatter.types';

import { SUPPORTED_UNITS } from '../unit-formatter.constants';

/**
 * Pass-through transformer for unitless or special case values.
 * Handles unit assignment without value transformation.
 * Clears units for boolean and string type conversions.
 *
 * @param {UnitValue} unitValue - The value to transform
 * @param {any} unitValue.value - The value (unchanged by this transformer)
 * @param {string} [unitValue.units] - The current units
 * @param {string} [newUnits] - Target units to assign
 * @returns {UnitValue} Value with updated units
 *   - For recognized units (psig, psi, temperatures, frequencies, time): assigns new units
 *   - For 'boolean' or 'string' types: clears units (empty string)
 *   - Default: clears units (empty string)
 *
 * @example
 * transformNone({ value: 42, units: 'count' }, 'psig')
 * // Returns: { value: 42, units: 'psig' }
 *
 * @example
 * transformNone({ value: 'Active', units: 'status' }, 'boolean')
 * // Returns: { value: 'Active', units: '' }
 *
 * @example
 * transformNone({ value: 100, units: 'raw' }, '°F')
 * // Returns: { value: 100, units: '°F' }
 *
 * @example
 * transformNone({ value: true, units: 'flag' }, 'unknown')
 * // Returns: { value: true, units: '' }
 */
export const transformNone = (unitValue: UnitValue, newUnits?: string): UnitValue => {
  switch (newUnits?.toLowerCase()) {
    case SUPPORTED_UNITS.PSIG:
    case SUPPORTED_UNITS.PSI:
    case SUPPORTED_UNITS.DEGREES_F:
    case SUPPORTED_UNITS.DEGREES_C:
    case SUPPORTED_UNITS.DEGREES_K:
    case SUPPORTED_UNITS.HZ:
    case SUPPORTED_UNITS.RPM:
    case SUPPORTED_UNITS.MIN:
    case SUPPORTED_UNITS.SEC:
    case SUPPORTED_UNITS.HRS:
      return {
        value: unitValue.value,
        units: newUnits,
      };
    case SUPPORTED_UNITS.BOOLEAN:
    case SUPPORTED_UNITS.STRING:
    default:
      return {
        value: unitValue.value,
        units: '',
      };
  }
};
